import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import router from '@/router';
import { refreshAuthSession } from '@/services/auth/tokenRefresh';
import { logout, LogoutReason } from '@/services/auth/logoutService';
import useAuthStore from '@/stores/auth';
import * as Sentry from '@sentry/vue';
import { logAppError } from '@/config/sentryConfig';

import type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError
} from 'axios';
import type { AuthSession } from '@aws-amplify/auth';

/**
 * Safely get an abort signal, checking for null and abort status.
 * Returns null if the controller is null or the signal is already aborted.
 * This prevents race conditions where abort() is called during mid-flight requests.
 */
const getSafeAbortSignal = (
  controller: AbortController | null
): AbortSignal | null => {
  if (!controller) return null;
  const signal = controller.signal;
  return signal.aborted ? null : signal;
};

// Extend Axios config to include _retry flag
interface RetryAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const apiClient: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Shared controller used to abort all in-flight requests before logout.
// Replaced with a fresh instance after each cancellation so new requests
// made post-login are not born already aborted.
let pendingRequestsController = new AbortController();

// Gate that blocks any NEW requests from being dispatched once logout starts.
// Prevents poll timers that fire during the logout window from reaching Bobby
// with a dying session (which causes a 302→Cognito CORS error).
// Reset by resetSigningOut() at the start of the next sign-in.
let isSigningOut = false;

export const cancelPendingRequests = () => {
  isSigningOut = true;
  pendingRequestsController.abort();
  pendingRequestsController = new AbortController();
};

export const resetSigningOut = () => {
  isSigningOut = false;
};

/**
 * Check if logout is currently in progress.
 * Used by other parts of the app (e.g., SSE) to avoid operations during logout.
 */
export const isLogoutInProgress = (): boolean => isSigningOut;

export { getSafeAbortSignal };

// Request interceptor for adding the auth token
apiClient.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    // Block any new requests once logout has started — prevents poll timers
    // that fire during the logout window from reaching Bobby with an expiring
    // session and receiving a 302→Cognito redirect (which causes a CORS error).
    if (isSigningOut) {
      const cancelError = new axios.CanceledError('Signing out');
      (cancelError as any).isSessionExpired = true;
      return Promise.reject(cancelError);
    }

    // Attach the logout-cancellation signal when the caller hasn't supplied one.
    // Use safe getter to prevent null/aborted signal issues during logout.
    if (!config.signal) {
      const safeSignal = getSafeAbortSignal(pendingRequestsController);
      if (safeSignal) {
        config.signal = safeSignal;
      }
    }

    if (config.signal?.aborted) {
      return Promise.reject(
        new axios.CanceledError('Request aborted before auth')
      );
    }

    try {
      // Get the current authenticated user session
      const session: AuthSession = await fetchAuthSession();
      // Extract JWT token (id or access token depending on your setup)
      const token: string | undefined = session.tokens?.idToken?.toString();

      if (token) {
        // Add token to headers
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // User is not authenticated or session expired
      // Log at info level since this is expected for unauthenticated users
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Auth check failed in interceptor, proceeding without token',
        level: 'info',
        data: { error: String(error) }
      });

      // If the error was a cancellation, propagate that specific error
      if (config.signal?.aborted) {
        return Promise.reject(new axios.CanceledError('Auth aborted'));
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor
// FLOW DOCUMENTATION:
// When a 401 occurs, this interceptor attempts token refresh. Three outcomes:
//
// 1. REFRESH SUCCEEDS, RETRY STILL 401
//    → Reject with original AxiosError (has .response.status === 401)
//    → main.js guard catches via: initError?.response?.status === 401 ✅
//
// 2. REFRESH FAILS (most common real-world case: Cognito session expired)
//    → Interceptor calls logout(SessionExpired) and marks error: isSessionExpired=true
//    → main.js guard catches via: initError?.isSessionExpired === true ✅
//    → UserLogin watcher shows reason=session_expired snackbar
//
// 3. LOGOUT ALREADY IN PROGRESS or REQUEST BLOCKED BY isSigningOut GATE
//    → Reject with CanceledError marked: isSessionExpired=true
//    → main.js guard catches via: initError?.isSessionExpired === true ✅
//
// All three paths mark the error so main.js recognizes SessionExpired vs InitializationError.
// This prevents overwriting the reason parameter with a generic initialization_error.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError): Promise<AxiosInstance> => {
    const originalRequest = error.config as RetryAxiosRequestConfig;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true; // Mark this request as already retried

      try {
        // If logout is already in progress, don't attempt token refresh.
        // This prevents trying to refresh with a session that's being torn down.
        if (isSigningOut) {
          const cancelError = new axios.CanceledError(
            'Logout in progress, aborting refresh'
          );
          (cancelError as any).isSessionExpired = true;
          return Promise.reject(cancelError);
        }

        // Try to refresh the session
        const session: AuthSession = await refreshAuthSession();

        // Get the new token
        const newToken: string | undefined =
          session.tokens?.idToken?.toString();

        if (newToken) {
          // Update the token in the failed request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request with the new token
          return apiClient(originalRequest);
        } else {
          throw new Error('No token available');
        }
      } catch (refreshError) {
        // If refresh fails, perform full logout and redirect

        // Only sign out and redirect when the current route requires authentication,
        // or when the user had an active session that has now expired.
        // Unauthenticated users on unguarded routes must not be interrupted
        // by a background API call failing due to no session.
        const currentRoute = router.currentRoute.value;
        const isUnguarded = currentRoute.meta?.unguarded === true;
        const isAuthenticated = useAuthStore().isUserAuthenticated;

        if (!isUnguarded || isAuthenticated) {
          try {
            await logout(undefined, LogoutReason.SessionExpired);
            // logout() has handled navigation with the reason parameter.
            // Don't reject here - let the resolved promise complete so the
            // navigation stays in place and isn't overwritten.
          } catch (logoutError) {
            // Logout should complete successfully. This catch handles unexpected errors.
            // logoutService already handles navigation with fallback, so this is rare.
            logAppError({
              error: logoutError,
              functionName: 'api.interceptor.response',
              section: 'logout_after_401_refresh'
            });
          }
        }

        // Mark error so main.js doesn't overwrite the SessionExpired logout reason
        (refreshError as any).isSessionExpired = true;
        return Promise.reject(refreshError);
      }
    } else if (
      error.response &&
      error.response.status === 401 &&
      originalRequest._retry
    ) {
      // Retry already attempted, but still getting 401
      // This means the token refresh didn't help - perform full logout
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Retry with refreshed token still got 401, performing logout',
        level: 'warning'
      });

      const currentRoute = router.currentRoute.value;
      const isUnguarded = currentRoute.meta?.unguarded === true;
      const isAuthenticated = useAuthStore().isUserAuthenticated;

      if (!isUnguarded || isAuthenticated) {
        try {
          await logout(undefined, LogoutReason.SessionExpired);
        } catch (logoutError) {
          logAppError({
            error: logoutError,
            functionName: 'api.interceptor.response',
            section: 'logout_after_retry_401'
          });
        }
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
